import {
  type CodeHeaderProps,
  MarkdownTextPrimitive,
  unstable_memoizeMarkdownComponents as memoizeMarkdownComponents,
  useIsMarkdownCodeBlock,
} from "@assistant-ui/react-markdown";
import remarkGfm from "remark-gfm";
import { type FC, memo, useState } from "react";
import { CheckIcon, CopyIcon } from "lucide-react";
import { useMessage } from "@assistant-ui/react";

import { OpenInNewOutlinedIcon } from "@/components/icons/chat-header-icons";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { cn } from "@/lib/utils";

const MarkdownTextImpl = () => {
  const message = useMessage();
  
  // Defense-in-depth: Filter handoff JSON that should only be visible to Genesys agents
  // This is a backup in case the socket layer filter didn't catch it
  // Only check assistant messages (role === "assistant")
  try {
    if (message?.role === "assistant") {
      const content = message.content;
      if (content && content.length > 0) {
        const textContent = content[0];
        if (textContent?.type === "text" && textContent.text) {
          const trimmed = textContent.text.trim();
          if (trimmed.startsWith("{") && trimmed.includes('"type":"handoff"')) {
            // Don't render handoff metadata JSON - it's for Genesys agents only
            return null;
          }
        }
      }
    }
  } catch (error) {
    // Fail gracefully - render the message even if filter check fails
    console.warn("[phoebe-chat] handoff filter error:", error);
  }
  
  return (
    <MarkdownTextPrimitive
      remarkPlugins={[remarkGfm]}
      className="aui-md"
      components={defaultComponents}
    />
  );
};

export const MarkdownText = memo(MarkdownTextImpl);

const CodeHeader: FC<CodeHeaderProps> = ({ language, code }) => {
  const { isCopied, copyToClipboard } = useCopyToClipboard();
  const onCopy = () => {
    if (!code || isCopied) return;
    copyToClipboard(code);
  };

  return (
    <div className="aui-code-header-root mt-2.5 flex items-center justify-between rounded-t-lg border border-border/50 border-b-0 bg-muted/50 px-3 py-1.5 text-xs">
      <span className="aui-code-header-language font-medium text-muted-foreground lowercase">
        {language}
      </span>
      <TooltipIconButton tooltip="Copy" onClick={onCopy}>
        {!isCopied && <CopyIcon />}
        {isCopied && <CheckIcon />}
      </TooltipIconButton>
    </div>
  );
};

const COPIED_RESET_DELAY_MS = 3000;

const useCopyToClipboard = ({
  copiedDuration = COPIED_RESET_DELAY_MS,
}: {
  copiedDuration?: number;
} = {}) => {
  const [isCopied, setIsCopied] = useState<boolean>(false);

  const copyToClipboard = (value: string) => {
    if (!value) return;

    navigator.clipboard.writeText(value).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), copiedDuration);
    });
  };

  return { isCopied, copyToClipboard };
};

const defaultComponents = memoizeMarkdownComponents({
  h1: ({ className, ...props }) => (
    <h1
      className={cn(
        "aui-md-h1 mb-2 scroll-m-20 font-['Roboto',sans-serif] text-[16px] leading-[24px] font-bold tracking-[0.15px] text-[#001823] first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  h2: ({ className, ...props }) => (
    <h2
      className={cn(
        "aui-md-h2 mt-3 mb-1.5 scroll-m-20 font-['Roboto',sans-serif] text-[16px] leading-[24px] font-bold tracking-[0.15px] text-[#001823] first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  h3: ({ className, ...props }) => (
    <h3
      className={cn(
        "aui-md-h3 mt-2.5 mb-1 scroll-m-20 font-['Roboto',sans-serif] text-[16px] leading-[24px] font-bold tracking-[0.15px] text-[#001823] first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  h4: ({ className, ...props }) => (
    <h4
      className={cn(
        "aui-md-h4 mt-2 mb-1 scroll-m-20 font-['Roboto',sans-serif] text-[16px] leading-[24px] font-bold tracking-[0.15px] text-[#001823] first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  h5: ({ className, ...props }) => (
    <h5
      className={cn(
        "aui-md-h5 mt-2 mb-1 font-['Roboto',sans-serif] text-[16px] leading-[24px] font-bold tracking-[0.15px] text-[#001823] first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  h6: ({ className, ...props }) => (
    <h6
      className={cn(
        "aui-md-h6 mt-2 mb-1 font-['Roboto',sans-serif] text-[16px] leading-[24px] font-bold tracking-[0.15px] text-[#001823] first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  p: ({ className, ...props }) => (
    <p
      className={cn(
        "aui-md-p my-2.5 max-w-[320px] font-['Roboto',sans-serif] text-[14px] leading-[20px] font-normal tracking-[0px] text-[#001823] wrap-break-word first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  a: ({ className, children, ...props }) => (
    <a
      className={cn(
        "aui-md-a inline-flex items-center gap-[2px] rounded-[4px] align-baseline font-['Roboto',sans-serif] text-[14px] leading-[20px] font-normal tracking-[0px] text-[#30718d] underline decoration-from-font underline-offset-auto transition-colors hover:text-[#b21f13] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#001823]",
        className,
      )}
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    >
      {children}
      <span aria-label="Opens in new window" className="inline-flex size-[14px] shrink-0" role="img">
        <OpenInNewOutlinedIcon className="size-[14px]" aria-hidden="true" />
      </span>
    </a>
  ),
  blockquote: ({ className, ...props }) => (
    <blockquote
      className={cn(
        "aui-md-blockquote my-2.5 border-muted-foreground/30 border-l-2 pl-3 text-muted-foreground italic",
        className,
      )}
      {...props}
    />
  ),
  ul: ({ className, ...props }) => (
    <ul
      className={cn(
        "aui-md-ul my-2 max-w-[320px] list-disc space-y-2 pl-[13px] font-['Roboto',sans-serif] text-[14px] leading-[20px] font-normal tracking-[0px] text-[#001823] marker:text-[#001823]",
        className,
      )}
      {...props}
    />
  ),
  ol: ({ className, ...props }) => (
    <ol
      className={cn(
        "aui-md-ol my-2 max-w-[320px] list-decimal space-y-2 pl-[20px] font-['Roboto',sans-serif] text-[14px] leading-[20px] font-normal tracking-[0px] text-[#001823] marker:text-[#001823] [&>li]:pl-[4px]",
        className,
      )}
      {...props}
    />
  ),
  hr: ({ className, ...props }) => (
    <hr
      className={cn("aui-md-hr my-2 border-muted-foreground/20", className)}
      {...props}
    />
  ),
  table: ({ className, ...props }) => (
    <table
      className={cn(
        "aui-md-table my-2 w-full border-separate border-spacing-0 overflow-y-auto",
        className,
      )}
      {...props}
    />
  ),
  th: ({ className, ...props }) => (
    <th
      className={cn(
        "aui-md-th bg-muted px-2 py-1 text-left font-medium first:rounded-tl-lg last:rounded-tr-lg [[align=center]]:text-center [[align=right]]:text-right",
        className,
      )}
      {...props}
    />
  ),
  td: ({ className, ...props }) => (
    <td
      className={cn(
        "aui-md-td border-muted-foreground/20 border-b border-l px-2 py-1 text-left last:border-r [[align=center]]:text-center [[align=right]]:text-right",
        className,
      )}
      {...props}
    />
  ),
  tr: ({ className, ...props }) => (
    <tr
      className={cn(
        "aui-md-tr m-0 border-b p-0 first:border-t [&:last-child>td:first-child]:rounded-bl-lg [&:last-child>td:last-child]:rounded-br-lg",
        className,
      )}
      {...props}
    />
  ),
  li: ({ className, ...props }) => (
    <li className={cn("aui-md-li leading-[20px] wrap-break-word", className)} {...props} />
  ),
  sup: ({ className, ...props }) => (
    <sup
      className={cn("aui-md-sup [&>a]:text-xs [&>a]:no-underline", className)}
      {...props}
    />
  ),
  pre: ({ className, ...props }) => (
    <pre
      className={cn(
        "aui-md-pre overflow-x-auto rounded-t-none rounded-b-lg border border-border/50 border-t-0 bg-muted/30 p-3 text-xs leading-relaxed",
        className,
      )}
      {...props}
    />
  ),
  code: function Code({ className, ...props }) {
    const isCodeBlock = useIsMarkdownCodeBlock();
    return (
      <code
        className={cn(
          !isCodeBlock &&
            "aui-md-inline-code rounded-md border border-border/50 bg-muted/50 px-1.5 py-0.5 font-mono text-[0.85em]",
          className,
        )}
        {...props}
      />
    );
  },
  CodeHeader,
});
